import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Col, Row } from "reactstrap";

import "@/assets/scss/modern-modal.css";
import HistoryList from "@/components/Common/HistoryList";
import InfoRow from "@/components/Common/Inforow";
import TransactionDetailsModal from "@/components/Common/Modals/TransactionDetailsModal";
import Section from "@/components/Common/Section";
import StaticTableContainer from "@/components/Common/StaticTableContainer";
import TransactionFilesList from "@/components/Common/TransactionFilesList";
import useCustomHook from "@/components/Hooks/useCustomHook";
import { formatDate } from "@/helpers/date_helper";

import ActionModal from "./ActionModal";
import { getItemsViewColumn } from "./Columns";

import { useGetOrderRequestLogsQuery } from "@/api/Endpoints/Globo/Transactions/OrderRequest/OrderRequestLogs";
import { useFindOrderRequestsQuery, useGetFilesOrderRequestsQuery } from "@/api/Endpoints/Globo/Transactions/OrderRequest/OrderRequests";

function TransactionDetailsTab({ data }) {
  const deliveryAddress = [data?.deliveryAddress, data?.deliveryAddressBarangay, data?.deliveryAddressCityName, data?.deliveryAddressProvinceName]
    .filter(Boolean)
    .join(", ");

  const columns = getItemsViewColumn();

  return (
    <div style={{ paddingTop: 4 }}>
      <Section title="Request Details">
        <Row>
          <Col lg={6}>
            <InfoRow icon="bx bx-user" label="Patient" value={data?.patientName} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-user-check" label="Doctor" value={data?.doctorName} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-plus-medical" label="Hospital" value={data?.hospitalName} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-heart-circle" label="Therapeutic Area" value={data?.therapeuticAreaName} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-calendar" label="Date Prescribed" value={formatDate(data?.prescribedDate)} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-calendar" label="Order Date" value={formatDate(data?.orderDate)} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-calendar" label="Delivery Date" value={formatDate(data?.deliveryDate)} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-calendar" label="Date Recieved" value={formatDate(data?.dateReceived)} />
          </Col>
        </Row>
      </Section>

      <Section title="Order Details">
        <Row>
          <Col lg={12}>
            <div className="mb-3 border p-3 rounded">
              <StaticTableContainer
                columns={columns}
                data={data?.items || []}
                divClass="table-responsive mb-1"
                tableClass="mb-0 align-top"
                theadClass="table-light"
              />
            </div>
          </Col>
        </Row>
      </Section>

      <Section title="Delivery Details">
        <Row>
          <Col lg={6}>
            <InfoRow icon="bx bx-phone-call" label="Patient Mobile No." value={data?.patientMobileNo} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-envelope" label="Patient Email Address" value={data?.patientEmailAddress} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-group" label="Authorized Representative" value={data?.authorizedRepresentative} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-phone-call" label="Mobile No." value={data?.authorizedRepresentativeContactNo} />
          </Col>
          <Col lg={12}>
            <InfoRow icon="bx bx-map" label="Delivery Address" value={deliveryAddress} />
          </Col>
        </Row>
      </Section>

      <Section title="Remarks">
        <Row>
          <Col lg={12}>
            <InfoRow icon="bx bx-message-square-detail" label="Remarks" value={data?.remarks} />
          </Col>
        </Row>
      </Section>

      <Section title="Status Details">
        <Row>
          <Col lg={6}>
            <InfoRow label="Date Submitted" value={formatDate(data.dateSubmitted)} icon="ri-calendar-2-line" />
          </Col>
          <Col lg={6}>
            <InfoRow label="Submitted By" value={data.submittedByName} icon="ri-user-3-line" />
          </Col>
          <Col lg={6}>
            <InfoRow label="Date Approved" value={formatDate(data.dateApproved)} icon="ri-calendar-2-line" />
          </Col>
          <Col lg={6}>
            <InfoRow label="Approved By" value={data.approvedByName} icon="ri-user-3-line" />
          </Col>
          <Col lg={6}>
            <InfoRow label="Date Received" value={formatDate(data.dateReceived)} icon="ri-calendar-2-line" />
          </Col>
          <Col lg={6}>
            <InfoRow label="Set as Receied By" value={data.completedByName} icon="ri-user-3-line" />
          </Col>
        </Row>
      </Section>
    </div>
  );
}

export default function ViewDetails({ show, onCloseClick, data, parentKey, onUpdateParentKey, refetchTotals }) {
  if (!show) return null;

  const TABS = useMemo(
    () => [
      { key: "requestInfo", label: "Request Details", icon: "ri-user-line", component: TransactionDetailsTab },
      {
        key: "history",
        label: "History",
        icon: "ri-shield-flash-line",
        component: () => (
          <HistoryList transactionId={data?.orderRequestId} useLogsQuery={useGetOrderRequestLogsQuery} idKey="orderRequestId" title="History" />
        ),
      },
      {
        key: "files",
        label: "Files",
        icon: "ri-folder-line",
        component: () => (
          <TransactionFilesList
            transactionId={data?.orderRequestId}
            useFilesQuery={useGetFilesOrderRequestsQuery}
            idKey="orderRequestId"
            title="Files"
          />
        ),
      },
    ],
    [data?.orderRequestId],
  );

  const { state, customFunction } = useCustomHook({
    tabRefreshKey: 0,
  });

  const { data: transactionData, refetch } = useFindOrderRequestsQuery(
    { orderRequestId: data?.orderRequestId },
    { skip: !data?.orderRequestId, refetchOnMountOrArgChange: show },
  );
  const findData = transactionData?.returnData;

  const { reset, control } = useForm({ defaultValues: {} });

  useEffect(() => {
    reset({
      ...data,
      ...findData,
    });
    customFunction.updateState({
      data: {
        ...data,
        ...findData,
      },
    });
  }, [show, data, findData, refetch, reset]);

  const handleActions = (bool, data) => {
    if (bool) {
      refetch();
      customFunction.updateToggle("toggleAction");
      customFunction.updateState({
        tabRefreshKey: state?.tabRefreshKey + 1,
      });
      onUpdateParentKey(parentKey + 1);
      refetchTotals();
    } else {
      customFunction.updateToggle("toggleAction", { ...findData, actionName: data });
    }
  };

  const BUTTONS = useMemo(
    () => [
      {
        label: "Return",
        icon: "ri-arrow-go-back-line",
        className: "emd-btn-warning",
        show: state?.data?.actions?.return,
        onClick: () => handleActions(false, "Return"),
      },
      {
        label: "Approve",
        icon: "ri-check-double-line",
        className: "emd-btn-success",
        show: state?.data?.actions?.approve,
        onClick: () => handleActions(false, "Approve"),
      },
      {
        label: "Receive",
        icon: "ri-truck-fill",
        className: "emd-btn-success",
        show: state?.data?.actions?.receive,
        onClick: () => handleActions(false, "Receive"),
      },
      {
        label: "Reject",
        icon: "ri-thumb-down-line",
        className: "emd-btn-danger",
        show: state?.data?.actions?.reject,
        onClick: () => handleActions(false, "Reject"),
      },
      {
        label: "Cancel",
        icon: "ri-close-line",
        className: "emd-btn-danger",
        show: state?.data?.actions?.cancel,
        onClick: () => handleActions(false, "Cancel"),
      },
    ],
    [state?.data?.actions],
  );

  return (
    <React.Fragment>
      <ActionModal
        state={state}
        customFunction={customFunction}
        orderRequestId={findData?.orderRequestId}
        data={state?.trxValue}
        show={state.toggle.toggleAction}
        onCloseClick={handleActions}
      />

      <TransactionDetailsModal
        title={"Orders / Availment Request"}
        show={show}
        data={state.data}
        tabs={TABS}
        buttons={BUTTONS}
        onCloseClick={onCloseClick}
        tabRefreshKey={state.tabRefreshKey}
      />
    </React.Fragment>
  );
}
